/**
 * Gaushala Profile Page - Tabbed Interface
 *
 * Clean, modern tabbed layout for displaying user and gaushala profile data
 * Tabs:
 * 1. Personal Details - User information from Auth Service
 * 2. Gaushala Details - Organization information
 * 3. Statistics - Cattle statistics
 * 4. Infrastructure - Infrastructure details
 * 5. Financial - Financial information
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Loader2,
  AlertCircle,
  User,
  Phone,
  Mail,
  Shield,
  Calendar,
  Building2,
  MapPin,
  Globe,
  Users,
  Beef,
  Leaf,
  Home,
  Droplet,
  Zap,
  DollarSign,
  X,
  ChevronLeft,
  CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getGaushalaProfile,
  GaushalaProfileResponse,
  PersonDetails,
  GaushalaDetails,
  GaushalaStatistics,
  formatCurrency,
  formatNumber,
  formatDecimal,
  GaushalaError,
} from '@/services/gaushala/profileService';
import ProfileDataGrid, { DataGridItem } from './components/ProfileDataGrid';
import { BankAccountDisplay } from '@/components/gaushala/BankAccountDisplay';

/**
 * Gaushala Profile Page Component
 */
export const GaushalaProfileTabbed: React.FC = () => {
  // Navigation and Auth
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  // State management
  const [profile, setProfile] = useState<GaushalaProfileResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [personDetails, setPersonDetails] = useState<PersonDetails | null>(null);
  const [gaushalaDetails, setGaushalaDetails] = useState<GaushalaDetails | null>(null);
  const [statistics, setStatistics] = useState<GaushalaStatistics | null>(null);
  const [activeTab, setActiveTab] = useState<string>('personal');

  /**
   * Fetch profile on component mount
   */
  useEffect(() => {
    fetchProfile();
  }, []);

  /**
   * Fetch complete profile data
   */
  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const profileData = await getGaushalaProfile();
      setProfile(profileData);
      setPersonDetails(profileData.personDetails || null);
      setGaushalaDetails(profileData.gaushalaDetails || null);
      setStatistics(profileData.statistics || null);

      if (profileData.personDetails) {
        toast.success('Profile loaded successfully');
      }
    } catch (err) {
      let errorMessage = 'Failed to load profile';

      if (err instanceof GaushalaError) {
        errorMessage = err.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Close profile and navigate back to dashboard
   */
  const handleCloseProfile = () => {
    navigate('/gaushala');
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  /**
   * Build Personal Details tab data - Only include fields with actual values
   */
  const getPersonalDetailsData = (): DataGridItem[] => {
    // Combine data from AuthContext (primary) and personDetails (fallback)
    // AuthContext has the complete user data from Auth Service
    // personDetails may have additional fields from Gaushala Service

    const fullName = authUser?.name || personDetails?.name || '—';
    const phoneNumber = authUser?.phone || personDetails?.phone || '—';
    const emailAddress = authUser?.email || personDetails?.email || '—';
    const userId = authUser?.id || personDetails?.id || '—';
    const accountCreated = personDetails?.createdAt || '—';
    const lastLogin = personDetails?.lastLogin || '—';
    const kycStatus = authUser?.kycStatus || personDetails?.kycStatus || '—';
    const accountStatus = authUser?.isActive !== undefined ? (authUser.isActive ? 'Active' : 'Inactive') : (personDetails?.isActive !== undefined ? (personDetails.isActive ? 'Active' : 'Inactive') : '—');

    return [
      { label: 'Full Name', value: fullName, icon: <User className="w-4 h-4" /> },
      { label: 'Phone Number', value: phoneNumber, icon: <Phone className="w-4 h-4" /> },
      { label: 'Email Address', value: emailAddress, icon: <Mail className="w-4 h-4" /> },
      { label: 'User ID', value: userId, icon: <Shield className="w-4 h-4" /> },
      { label: 'Account Created', value: accountCreated, icon: <Calendar className="w-4 h-4" />, format: (val) => val === '—' ? '—' : formatDate(val) },
      { label: 'Last Login', value: lastLogin, icon: <Calendar className="w-4 h-4" />, format: (val) => val === '—' ? '—' : formatDate(val) },
      { label: 'KYC Status', value: kycStatus },
      { label: 'Account Status', value: accountStatus },
    ];
  };

  /**
   * Build Gaushala Details tab data - Only include fields with actual values
   */
  const getGaushalaDetailsData = (): DataGridItem[] => {
    if (!gaushalaDetails) return [];
    return [
      ...(gaushalaDetails.gaushalaName ? [{ label: 'Gaushala Name', value: gaushalaDetails.gaushalaName, icon: <Building2 className="w-4 h-4" /> }] : []),
      ...(gaushalaDetails.registrationNumber ? [{ label: 'Registration Number', value: gaushalaDetails.registrationNumber }] : []),
      ...(gaushalaDetails.organizationType ? [{ label: 'Organization Type', value: gaushalaDetails.organizationType }] : []),
      ...(gaushalaDetails.yearEstablished ? [{ label: 'Year Established', value: gaushalaDetails.yearEstablished }] : []),
      ...(gaushalaDetails.registeredAddress ? [{ label: 'Registered Address', value: gaushalaDetails.registeredAddress }] : []),
      ...(gaushalaDetails.website ? [{ label: 'Website', value: gaushalaDetails.website, icon: <Globe className="w-4 h-4" /> }] : []),
      ...(gaushalaDetails.email ? [{ label: 'Email', value: gaushalaDetails.email, icon: <Mail className="w-4 h-4" /> }] : []),
      ...(gaushalaDetails.contactNumber ? [{ label: 'Contact Number', value: gaushalaDetails.contactNumber, icon: <Phone className="w-4 h-4" /> }] : []),
      ...(gaushalaDetails.headName ? [{ label: 'Head Name', value: gaushalaDetails.headName, icon: <Users className="w-4 h-4" /> }] : []),
      ...(gaushalaDetails.headMobileNumber ? [{ label: 'Head Mobile', value: gaushalaDetails.headMobileNumber, icon: <Phone className="w-4 h-4" /> }] : []),
      ...(gaushalaDetails.headEmail ? [{ label: 'Head Email', value: gaushalaDetails.headEmail, icon: <Mail className="w-4 h-4" /> }] : []),
    ];
  };

  /**
   * Build Statistics tab data - Only include fields with actual values
   */
  const getStatisticsData = (): DataGridItem[] => {
    if (!statistics?.cattle) return [];
    return [
      ...(statistics.cattle.totalCattle !== null && statistics.cattle.totalCattle !== undefined ? [{ label: 'Total Cattle', value: statistics.cattle.totalCattle, icon: <Beef className="w-4 h-4" />, format: formatNumber }] : []),
      ...(statistics.cattle.totalCows !== null && statistics.cattle.totalCows !== undefined ? [{ label: 'Total Cows', value: statistics.cattle.totalCows, icon: <Beef className="w-4 h-4" />, format: formatNumber }] : []),
      ...(statistics.cattle.totalBulls !== null && statistics.cattle.totalBulls !== undefined ? [{ label: 'Total Bulls', value: statistics.cattle.totalBulls, icon: <Beef className="w-4 h-4" />, format: formatNumber }] : []),
      ...(statistics.cattle.totalCalves !== null && statistics.cattle.totalCalves !== undefined ? [{ label: 'Total Calves', value: statistics.cattle.totalCalves, icon: <Beef className="w-4 h-4" />, format: formatNumber }] : []),
      ...(statistics.cattle.milkingCows !== null && statistics.cattle.milkingCows !== undefined ? [{ label: 'Milking Cows', value: statistics.cattle.milkingCows, icon: <Beef className="w-4 h-4" />, format: formatNumber }] : []),
      ...(statistics.cattle.avgMilkProduction !== null && statistics.cattle.avgMilkProduction !== undefined ? [{ label: 'Avg Milk Production', value: statistics.cattle.avgMilkProduction, format: (v) => `${formatDecimal(v)} L/day` }] : []),
    ];
  };

  /**
   * Build Infrastructure tab data - Only include fields with actual values
   */
  const getInfrastructureData = (): DataGridItem[] => {
    if (!statistics?.infrastructure) return [];
    return [
      ...(statistics.infrastructure.totalLandArea !== null && statistics.infrastructure.totalLandArea !== undefined ? [{ label: 'Total Land Area', value: statistics.infrastructure.totalLandArea, icon: <Leaf className="w-4 h-4" />, format: (v) => `${formatNumber(v)} m²` }] : []),
      ...(statistics.infrastructure.shelterCapacity !== null && statistics.infrastructure.shelterCapacity !== undefined ? [{ label: 'Shelter Capacity', value: statistics.infrastructure.shelterCapacity, icon: <Home className="w-4 h-4" />, format: formatNumber }] : []),
      ...(statistics.infrastructure.waterSource ? [{ label: 'Water Source', value: statistics.infrastructure.waterSource, icon: <Droplet className="w-4 h-4" /> }] : []),
      ...(statistics.infrastructure.electricityAvailable !== null && statistics.infrastructure.electricityAvailable !== undefined ? [{ label: 'Electricity Available', value: statistics.infrastructure.electricityAvailable, format: (v) => v ? 'Yes' : 'No' }] : []),
    ];
  };

  /**
   * Build Financial tab data - Only include fields with actual values
   */
  const getFinancialData = (): DataGridItem[] => {
    if (!statistics?.financial) return [];
    return [
      ...(statistics.financial.annualBudget !== null && statistics.financial.annualBudget !== undefined ? [{ label: 'Annual Budget', value: statistics.financial.annualBudget, icon: <DollarSign className="w-4 h-4" />, format: formatCurrency }] : []),
      ...(statistics.financial.sourceOfFunds ? [{ label: 'Source of Funds', value: statistics.financial.sourceOfFunds }] : []),
    ];
  };

  /**
   * Loading state
   */
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  /**
   * No gaushala state
   */
  if (personDetails && !gaushalaDetails && !error) {
    return (
      <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <div className="w-full h-full flex flex-col gap-6">
            {/* No Gaushala Banner */}
            <Alert className="border-blue-300 bg-blue-50 rounded-xl flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div className="ml-3 flex-1">
                <h3 className="font-semibold text-blue-900">No Gaushala Assigned</h3>
                <p className="text-sm text-blue-800 mt-1">
                  No Gaushala data is currently associated with your account. Please contact your administrator to assign a Gaushala.
                </p>
              </div>
            </Alert>

            {/* Personal Details Card */}
            <Card className="p-6 lg:p-8 bg-white rounded-xl shadow-md flex-1 flex flex-col overflow-hidden">
              <div className="flex flex-col sm:flex-row gap-6 mb-8 pb-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex-shrink-0">
                  {personDetails?.profilePhoto ? (
                    <img
                      src={personDetails.profilePhoto}
                      alt={personDetails.name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-blue-500 shadow-md"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center border-4 border-blue-500 shadow-md">
                      <User className="w-12 h-12 text-blue-600" />
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">{personDetails?.name || '—'}</h2>
                  <p className="text-gray-600 mb-4 text-lg">{personDetails?.phone || '—'}</p>
                  <div className="flex flex-wrap gap-2">
                    {personDetails?.kycStatus && (
                      <Badge className="bg-blue-100 text-blue-800 border-0 text-sm font-medium">{personDetails.kycStatus}</Badge>
                    )}
                    {personDetails?.isActive && (
                      <Badge className="bg-green-100 text-green-800 border-0 text-sm font-medium">Active</Badge>
                    )}
                    {personDetails?.roles?.[0] && (
                      <Badge className="bg-purple-100 text-purple-800 border-0 text-sm font-medium">{personDetails.roles[0]}</Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="overflow-y-auto flex-1">
                <ProfileDataGrid
                  title="Personal Information"
                  subtitle="Your account details and status"
                  items={getPersonalDetailsData()}
                  columns={2}
                />

                {/* Roles Section */}
                {personDetails?.roles && personDetails.roles.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Assigned Roles</h3>
                    <div className="flex flex-wrap gap-2">
                      {personDetails.roles.map((role, idx) => (
                        <Badge key={idx} className="bg-blue-100 text-blue-800 border-0 px-3 py-1 text-sm">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Permissions Section */}
                {personDetails?.permissions && personDetails.permissions.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Permissions</h3>
                    <div className="flex flex-wrap gap-2">
                      {personDetails.permissions.map((perm, idx) => (
                        <Badge key={idx} variant="outline" className="border-gray-300 bg-gray-50 px-3 py-1 text-sm">
                          {perm}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Error state
   */
  if (error && !personDetails) {
    const isNoGaushalaError = error.toLowerCase().includes('gaushala') && error.toLowerCase().includes('not assigned');

    return (
      <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Error Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 flex items-center justify-center">
          <Card className="p-6 lg:p-8 bg-white rounded-xl shadow-md w-full max-w-md">
            <Alert variant="destructive" className={`mb-6 border-3 rounded-lg ${isNoGaushalaError ? 'border-yellow-300 bg-yellow-50' : 'border-red-300 bg-red-50'}`}>
              <AlertCircle className={`h-5 w-5 flex-shrink-0 ${isNoGaushalaError ? 'text-yellow-700' : 'text-red-700'}`} />
              <div className={`ml-3 flex-1 ${isNoGaushalaError ? 'text-yellow-900' : 'text-red-900'}`}>
                <h3 className="font-semibold">{isNoGaushalaError ? 'Gaushala Not Assigned' : 'Error Loading Profile'}</h3>
                <p className="text-sm mt-2">{error}</p>
              </div>
            </Alert>

            {!isNoGaushalaError && (
              <Button onClick={fetchProfile} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg mb-3">
                <Loader2 className="w-4 h-4 mr-2" />
                Retry
              </Button>
            )}

            {isNoGaushalaError && (
              <p className="text-sm text-gray-700">
                Please contact your administrator with your phone number to get a gaushala assigned to your account.
              </p>
            )}
          </Card>
        </div>
      </div>
    );
  }

  /**
   * Main tabbed profile display
   */
  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Main Content - Flexbox for full height */}
      <div className="flex-1 overflow-hidden px-0 py-4 lg:py-6">
        <div className="w-full h-full flex flex-col gap-4">
          {/* Profile Header Card */}
          <Card className="p-6 lg:p-8 bg-white rounded-lg shadow-sm border border-gray-200 flex-shrink-0 mx-4 sm:mx-6 lg:mx-8 relative">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseProfile}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 h-9 w-9 rounded-lg transition-all"
              title="Close profile"
            >
              <X className="w-5 h-5" />
            </Button>

            <div className="flex flex-col sm:flex-row gap-8">
              {/* Profile Photo */}
              <div className="flex-shrink-0">
                {personDetails?.profilePhoto ? (
                  <img
                    src={personDetails.profilePhoto}
                    alt={personDetails?.name}
                    className="w-32 h-32 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-blue-600 shadow-lg"
                  />
                ) : (
                  <div className="w-32 h-32 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center border-4 border-blue-600 shadow-lg">
                    <User className="w-16 h-16 sm:w-14 sm:h-14 text-blue-600" />
                  </div>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1">
                <div className="mb-6">
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    {authUser?.name || personDetails?.name || 'Guest User'}
                  </h2>
                  <p className="text-gray-600 text-base font-medium">{authUser?.role || 'User'}</p>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-3">
                  {personDetails?.kycStatus && (
                    <Badge className="bg-blue-100 text-blue-800 border-0 text-sm font-semibold px-3 py-1.5">
                      {personDetails.kycStatus}
                    </Badge>
                  )}
                  {personDetails?.isActive && (
                    <Badge className="bg-green-100 text-green-800 border-0 text-sm font-semibold px-3 py-1.5">
                      Active
                    </Badge>
                  )}
                  {gaushalaDetails && (
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-sm font-semibold px-3 py-1.5">
                      {gaushalaDetails.gaushalaName}
                    </Badge>
                  )}
                  {personDetails?.roles?.map((role, idx) => (
                    <Badge key={idx} className="bg-purple-100 text-purple-800 border-0 text-sm font-semibold px-3 py-1.5">
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Refresh Button */}
              {error && (
                <Button onClick={fetchProfile} variant="outline" className="self-start lg:self-center h-fit">
                  <Loader2 className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              )}
            </div>

            {/* Separator */}
            <Separator className="mt-8" />
          </Card>

          {/* Tabs Section - Full Width and Height */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col px-4 sm:px-6 lg:px-8">
            {/* Tab List */}
            <div className="mb-0 flex-shrink-0">
              <TabsList className="bg-white border-b border-gray-200 rounded-none p-0 shadow-none w-full justify-start overflow-x-auto">
                <TabsTrigger
                  value="personal"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none text-gray-600 font-semibold px-4 py-3 whitespace-nowrap hover:text-gray-900 transition-colors"
                >
                  Personal Details
                </TabsTrigger>

                {gaushalaDetails && (
                  <>
                    <TabsTrigger
                      value="gaushala"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none text-gray-600 font-semibold px-4 py-3 whitespace-nowrap hover:text-gray-900 transition-colors"
                    >
                      Gaushala Details
                    </TabsTrigger>

                    <TabsTrigger
                      value="statistics"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none text-gray-600 font-semibold px-4 py-3 whitespace-nowrap hover:text-gray-900 transition-colors"
                    >
                      Statistics
                    </TabsTrigger>

                    <TabsTrigger
                      value="infrastructure"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none text-gray-600 font-semibold px-4 py-3 whitespace-nowrap hover:text-gray-900 transition-colors"
                    >
                      Infrastructure
                    </TabsTrigger>

                    <TabsTrigger
                      value="financial"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none text-gray-600 font-semibold px-4 py-3 whitespace-nowrap hover:text-gray-900 transition-colors"
                    >
                      Financial
                    </TabsTrigger>

                    <TabsTrigger
                      value="bankaccount"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600 data-[state=active]:shadow-none text-gray-600 font-semibold px-4 py-3 whitespace-nowrap hover:text-gray-900 transition-colors"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Bank Account
                    </TabsTrigger>
                  </>
                )}
              </TabsList>
            </div>

            {/* Tab Content Cards - Full Width and Height */}
            <Card className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 flex flex-col overflow-hidden mt-4">
              {/* Personal Details Tab */}
              <TabsContent value="personal" className="p-6 lg:p-8 overflow-y-auto flex-1 h-full">
                <ProfileDataGrid
                  title="Personal Information"
                  subtitle="Your account details and status"
                  items={getPersonalDetailsData()}
                  columns={2}
                />

                {/* Roles and Permissions */}
                {(personDetails?.roles?.length || personDetails?.permissions?.length) && (
                  <>
                    {personDetails?.roles && personDetails.roles.length > 0 && (
                      <div className="mt-12 pt-8 border-t border-gray-200">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Assigned Roles</h3>
                        <div className="flex flex-wrap gap-3">
                          {personDetails.roles.map((role, idx) => (
                            <Badge key={idx} className="bg-blue-100 text-blue-800 border-0 px-3 py-1.5 text-sm font-semibold">
                              {role}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {personDetails?.permissions && personDetails.permissions.length > 0 && (
                      <div className="mt-12 pt-8 border-t border-gray-200">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Permissions</h3>
                        <div className="flex flex-wrap gap-3">
                          {personDetails.permissions.map((perm, idx) => (
                            <Badge key={idx} variant="outline" className="border-gray-300 bg-gray-50 px-3 py-1.5 text-sm font-semibold">
                              {perm}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>

              {/* Gaushala Details Tab */}
              {gaushalaDetails && (
                <TabsContent value="gaushala" className="p-6 lg:p-8 overflow-y-auto flex-1 h-full">
                  <ProfileDataGrid
                    title="Gaushala Information"
                    subtitle="Organization and management details"
                    items={getGaushalaDetailsData()}
                    columns={2}
                  />
                </TabsContent>
              )}

              {/* Statistics Tab */}
              {statistics?.cattle && (
                <TabsContent value="statistics" className="p-6 lg:p-8 overflow-y-auto flex-1 h-full">
                  <ProfileDataGrid
                    title="Cattle Statistics"
                    subtitle="Livestock count and production details"
                    items={getStatisticsData()}
                    columns={2}
                  />
                </TabsContent>
              )}

              {/* Infrastructure Tab */}
              {statistics?.infrastructure && (
                <TabsContent value="infrastructure" className="p-6 lg:p-8 overflow-y-auto flex-1 h-full">
                  <ProfileDataGrid
                    title="Infrastructure Details"
                    subtitle="Land, facilities, and utilities information"
                    items={getInfrastructureData()}
                    columns={2}
                  />
                </TabsContent>
              )}

              {/* Financial Tab */}
              {statistics?.financial && (
                <TabsContent value="financial" className="p-6 lg:p-8 overflow-y-auto flex-1 h-full">
                  <ProfileDataGrid
                    title="Financial Information"
                    subtitle="Budget and funding details"
                    items={getFinancialData()}
                    columns={1}
                  />
                </TabsContent>
              )}

              {/* Bank Account Tab */}
              {gaushalaDetails && (
                <TabsContent value="bankaccount" className="p-6 lg:p-8 overflow-y-auto flex-1 h-full">
                  <BankAccountDisplay
                    showEditButton={true}
                    compact={false}
                  />
                </TabsContent>
              )}

              {/* Last Updated - Sticky Footer */}
              {gaushalaDetails?.updatedAt && (
                <div className="text-center text-sm text-gray-500 border-t border-gray-200 px-6 lg:px-8 py-4 flex-shrink-0 bg-gray-50">
                  Last updated: {formatDate(gaushalaDetails.updatedAt)}
                </div>
              )}
            </Card>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default GaushalaProfileTabbed;
